import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Mic, Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import api from '../utils/axiosConfig';

const ConnectOnboarding = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  const refreshStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/stripe/connect/status');
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    const accountId = searchParams.get('accountId');
    const refresh = searchParams.get('refresh');
    if (accountId || refresh) {
      refreshStatus();
    }
  }, [searchParams]);

  const handleCreateAndOnboard = async () => {
    try {
      setCreating(true);
      setError(null);
      await api.post('/api/stripe/connect/create-account');
      setCreating(false);
      await handleGetLink();
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
      setCreating(false);
    }
  };

  const handleGetLink = async () => {
    try {
      setLinking(true);
      setError(null);
      const res = await api.post('/api/stripe/connect/account-link');
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
      setLinking(false);
    }
  };

  if (loading && !status) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='max-w-xl mx-auto p-6'>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Mic className='h-5 w-5' />
            {t('connect.title')}
          </CardTitle>
          <CardDescription>{t('connect.description')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {error && (
            <div className='flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              {error}
            </div>
          )}

          {status?.hasAccount && (
            <div className='flex flex-wrap gap-2'>
              <Badge variant={status.readyToReceivePayments ? 'default' : 'secondary'}>
                {status.readyToReceivePayments ? (
                  <>
                    <CheckCircle className='h-3 w-3 mr-1' />
                    {t('connect.readyToReceive')}
                  </>
                ) : (
                  t('connect.notReady')
                )}
              </Badge>
              <Badge variant={status.onboardingComplete ? 'default' : 'outline'}>
                {status.onboardingComplete ? t('connect.onboardingComplete') : t('connect.onboardingIncomplete')}
              </Badge>
            </div>
          )}

          <div className='flex flex-col gap-2'>
            {!status?.hasAccount ? (
              <Button onClick={handleCreateAndOnboard} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    {t('connect.creating')}
                  </>
                ) : (
                  <>
                    <ExternalLink className='h-4 w-4 mr-2' />
                    {t('connect.onboardToCollect')}
                  </>
                )}
              </Button>
            ) : !status?.onboardingComplete ? (
              <Button onClick={handleGetLink} disabled={linking}>
                {linking ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    {t('connect.redirecting')}
                  </>
                ) : (
                  <>
                    <ExternalLink className='h-4 w-4 mr-2' />
                    {t('connect.continueOnboarding')}
                  </>
                )}
              </Button>
            ) : status?.readyToReceivePayments ? (
              <p className='text-sm text-muted-foreground'>{t('connect.allSet')}</p>
            ) : (
              <Button onClick={handleGetLink} disabled={linking} variant='outline'>
                {linking ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  t('connect.updateAccount')
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectOnboarding;
